import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { CardStackSummary, LegacyCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import lessonViewAction from "cc-frontend/lib/actions/lesson/LESSON_VIEW"
import type { AiValueEditor } from "cc-frontend/lib/ai/ai-value-editor"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type Session from "cc-frontend/services/session"
import { formatCourseTime } from "cc-frontend/utils/course/times"
import { isArray, isNil, map, sortBy } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./legacy-lesson-editable.module.css"

interface Signature {
  Args: {
    models: {
      courseDate: CourseDateCombined
      course: Course
      lesson: LegacyCardStack
      units: Array<CardStackSummary>
      rotationCalendar: RotationCalendar
      planbook: Planbook
      dateString: string
    }
    editCourseTitleAndOrder: Function
    lessonsCollapsed: boolean
    perspective: "day" | "week" | "month"
    isFullScreen?: boolean
    sortableParent?: unknown
  }
}

export default class LegacyLessonEditableComponent extends Component<Signature> {
  readonly styles = styles

  @service declare session: Session
  @service declare dispatcher: Dispatcher
  @service declare lessonEditor: LessonEditor

  //state
  @tracked lessonIsExpanded: boolean = false
  @tracked hasTrackedView: boolean = false
  @tracked showAI: boolean = false

  /**
   * AiValueEditor for each relevant card, keyed by `${card.id}:${prop}`,
   * as well as the lesson title's editor keyed by "title".
   */
  readonly aiValueEditors = new Map<string, AiValueEditor<unknown>>()

  @action expandThisLesson() {
    this.lessonIsExpanded = true
  }

  @action collapseThisLesson() {
    this.lessonIsExpanded = false
  }

  @computed(
    "args.models.rotationCalendar",
    "args.models.planbook",
    "args.models.course",
    "args.models.courseDate.dateString",
    "args.models.lesson",
  )
  get modelsNeededForFirstEdit(): ModelsNeededForFirstEdit {
    return {
      rotationCalendar: this.args.models.rotationCalendar,
      planbook: this.args.models.planbook,
      course: this.args.models.course,
      dateString: this.args.models.courseDate.dateString,
      // This component only ever renders lessons.
      cardStack: this.args.models.lesson as LegacyCardStack & { attributes: { usedAs: "lesson" } },
    }
  }

  @computed("args.models.lesson.attributes.cards.[]")
  get cardAttributes() {
    if (this.args.models === undefined || isNil(this.args.models.lesson)) return undefined
    let cards = this.args.models.lesson.attributes.cards
    return map(cards, "attributes")
  }

  @computed("cardAttributes.@each.position")
  get sortedCards() {
    let cardAttributes = this.cardAttributes // have to trigger the computed property above
    if (
      isArray(cardAttributes) !== true ||
      isNil(this.args.models.lesson) ||
      isNil(this.args.models.lesson.attributes)
    )
      return undefined
    let cards = this.args.models.lesson.attributes.cards
    return sortBy(cards, "attributes.position")
  }

  @computed(
    "args.models.courseDate.isNewLesson",
    "args.models.course.attributes.isNonInstructional",
  )
  get showAsNewLesson(): boolean {
    return (
      this.args.models.courseDate.isNewLesson &&
      !this.args.models.course.attributes.isNonInstructional
    )
  }

  // TIMES

  @computed("args.models.courseDate.timeDefault")
  get hasTime() {
    return this.args.models.courseDate.timeDefault !== null
  }

  @computed("args.models.courseDate.timeCustom", "args.models.courseDate.timeDefault")
  get timeFormatted() {
    const time = this.args.models.courseDate.timeCustom ?? this.args.models.courseDate.timeDefault
    return formatCourseTime(time)
  }

  // Track view

  trackView = (cardStack) => {
    if (this.isDestroyed || this.isDestroying) return

    if (this.hasTrackedView === false && !cardStack.meta.isNewLesson) {
      this.dispatcher.dispatchPreparedAction(
        lessonViewAction({
          cardStackId: cardStack.id,
          lessonTitle: cardStack.attributes.title,
          planbookId: this.args.models.planbook.id,
          courseId: this.args.models.course.id,
          useStudentVersion: false,
        }),
      )
      this.hasTrackedView = true
    }
  }

  // AI

  @action
  registerTitleEditor(editor: AiValueEditor<unknown>) {
    return this.registerAiValueEditor("title", editor)
  }

  @action
  registerCardEditor(cardId: string, prop: string, editor: AiValueEditor<unknown>) {
    return this.registerAiValueEditor(`${cardId}:${prop}`, editor)
  }

  private registerAiValueEditor(key: string, editor: AiValueEditor<unknown>) {
    this.aiValueEditors.set(key, editor)
    return () => {
      // A replacement editor may have registered under this key before we were torn down.
      if (this.aiValueEditors.get(key) === editor) this.aiValueEditors.delete(key)
    }
  }

  @action
  toggleAI() {
    this.lessonEditor.createBlankLesson(this.modelsNeededForFirstEdit)
    this.showAI = !this.showAI
  }

  @action
  closeAI() {
    this.showAI = false
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::CardStacks::LegacyLessonEditable": typeof LegacyLessonEditableComponent
  }
}
